'use client';
import Image from '@/components/Image';
import { useMediaQuery } from '@mui/material';
import { useTranslations } from 'next-intl';
import { useRouter } from 'next/navigation';
import React from 'react';

const FillOutPrescriptionClient: React.FC = () => {
  const router = useRouter();
  const t = useTranslations('fillOutPrescription');
  const isMobile = useMediaQuery('(max-width: 768px)');

  const handleClick = () => {
    router.push('/guide-tips/measure-PD');
  };

  const prescriptionsImgsMap = {
    get singleVision() {
      return isMobile ? '1-de-m.png' : '1-de-pc.png';
    },
    get singleVisionWithPrism() {
      return isMobile ? '2-de-m.png' : '2-de-pc.png';
    },
    get progressive() {
      return isMobile ? '3-de-m.png' : '3-de-pc.png';
    },
    get reading() {
      return isMobile ? '4-de-m.png' : '4-de-pc.png';
    },
    get otherInfo() {
      return isMobile ? '5-de-m.png' : '5-de-pc.png';
    },
  };
  return (
    <div className="md:w-4/5">
      <h1 className="text-4xl font-bb mb-[48px]">{t('title')}</h1>

      <div className="mb-[85px]">
        <h2 className="text-[18px] md:text-2xl font-bb mb-[12px]">{t('singleVision.title')}</h2>
        <p className="text-[14px] md:text-base text-auxiliary-text md:text-primary-black leading-[30px] mb-8">
          {t('singleVision.description')}
        </p>
        <div className="grid grid-cols-1 md:grid-cols-1 gap-16 mt-16 md:mt-30">
          {
            <Image
              src={`/static-page/guide-tips/fill-out-prescription/${prescriptionsImgsMap.singleVision}`}
              alt={t('singleVisionPrescriptionExample1')}
              className="w-full"
              width={800}
              height={600}
            />
          }
        </div>
      </div>

      <div className="mb-[85px]">
        <h2 className="text-[18px] md:text-2xl font-bb mb-[12px]">
          {t('singleVisionWithPrism.title')}
        </h2>
        <p className="text-[14px] md:text-base text-auxiliary-text md:text-primary-black leading-[30px] mb-8">
          {t('singleVisionWithPrism.description')}
          (Hinweis: Im Rezept ist kein PD-Wert angegeben, aber Du musst Deinen PD-Wert ausfüllen.)
          <span
            onClick={handleClick}
            className="border-b border-primary-purple text-primary-purple cursor-pointer ml-1"
          >
            {t('howToMeasurePD')}
          </span>
        </p>
        <div className="grid grid-cols-1 md:grid-cols-1 gap-4 mt-16 md:mt-30">
          <Image
            src={`/static-page/guide-tips/fill-out-prescription/${prescriptionsImgsMap.singleVisionWithPrism}`}
            alt={t('singleVisionPrescriptionWithPrismExample1')}
            className="w-full"
            width={800}
            height={600}
          />
        </div>
      </div>

      <div className="mb-[85px]">
        <h2 className="text-[18px] md:text-2xl font-bb mb-[12px]">{t('progressive.title')}</h2>
        <ul className="text-[14px] md:text-base    md:text-primary-black leading-[30px] mb-8 space-y-2">
          <li>
            Wenn Dein Rezept wie die folgenden ist, musst Du die Informationen wie im Beispiel unten
            ausfüllen. (Hinweis: Im Rezept ist kein PD-Wert angegeben, aber Du musst Deinen PD-Wert
            ausfüllen.)
            <span
              onClick={handleClick}
              className="border-b border-primary-purple text-primary-purple cursor-pointer ml-1"
            >
              {t('howToMeasurePD')}
            </span>
          </li>
          <li className="mb-16">Beispiel 1: Du musst nur die entsprechenden Werte eintragen.</li>
          <li className="mb-16">
            Beispiel 2: Du musst +225 in +2,25, -150 in -1,50, -125 in -1,25, -50 in -0,50 ändern
            und dann die entsprechenden Werte eintragen.
          </li>
          <li className="mb-16">
            Beispiel 3: Du musst Nah O.D. +0,75 - Entfernung O.D. -1,50 oder Nah O.S. +1,00 -
            Entfernung O.S. -1,25 verwenden, um den ADD +2,25 Wert zu erhalten und dann das ADD-Feld
            auszufüllen.
          </li>
          <li className="mb-16">
            PS: Die drei verschiedenen Rezepte erhalten die gleichen verschreibungspflichtigen
            Gläser.
          </li>
        </ul>

        <div className="grid grid-cols-1 md:grid-cols-1 gap-16 mt-16 md:mt-30">
          <Image
            src={`/static-page/guide-tips/fill-out-prescription/${prescriptionsImgsMap.progressive}`}
            alt={t('progressivePrescriptionExample1')}
            className="w-full"
            width={800}
            height={600}
          />
        </div>
      </div>

      <div className="mb-[85px]">
        <h2 className="text-[18px] md:text-2xl font-bb mb-[12px]">{t('reading.title')}</h2>
        <p className="text-[14px] md:text-base text-auxiliary-text md:text-primary-black leading-[30px] mb-8">
          {t('reading.description')}(Hinweis: Im Rezept ist kein PD-Wert angegeben, aber Du musst
          Deinen PD-Wert ausfüllen.)
        </p>
        <div className="grid grid-cols-1 md:grid-cols-1 lg:grid-cols-1 gap-4 mt-16 md:mt-30">
          <Image
            src={`/static-page/guide-tips/fill-out-prescription/${prescriptionsImgsMap.reading}`}
            alt={t('readingPrescriptionExample2')}
            className="w-full"
            width={800}
            height={600}
          />
        </div>
      </div>

      <div className="mb-[180px]">
        <h2 className="text-[18px] md:text-2xl font-bb mb-[12px]">{t('otherInfo.title')}</h2>
        <ul className="text-[14px] md:text-base text-auxiliary-text md:text-primary-black leading-[30px] mb-8 space-y-2">
          <li>{t('hereAreAFewTermsYouMaySeeOnYourPrescriptionAndWhatTheyMean')}</li>
          <li>{t('odOculusDexterRightEye')}</li>
          <li>{t('osOculusSinisterLeftEye')}</li>
          <li>{t('plPlanoNoSphericalCorrectionNeededEnter0')}</li>
          <li>{t('dsDiopterSphereNoAstigmatismCorrectionForThisEye')}</li>
          <li>
            {t(
              'pleaseMakeSureToEnterAllValuesAsShownInTheExampleAgainIfYourPrescriptionDoesntListAPDYoullNeedToProvideIt',
            )}
            <span
              onClick={handleClick}
              className="border-b border-primary-purple text-primary-purple cursor-pointer ml-1"
            >
              <br />
              {t('howToMeasurePD')}
            </span>
          </li>
        </ul>
        <div className="grid grid-cols-1 md:grid-cols-1 gap-16 mt-16 md:mt-30">
          <Image
            src={`/static-page/guide-tips/fill-out-prescription/${prescriptionsImgsMap.otherInfo}`}
            alt={t('otherPrescriptionInformationExample1')}
            className="w-full"
            width={800}
            height={600}
          />
        </div>
      </div>
    </div>
  );
};

export default FillOutPrescriptionClient;
